// ─── PDF 列印寬度與 CSS ───────────────────────────────────────────────────────
//
// PRINT_WIDTH 選定為 820px
// ─────────────────────────────────────────────────────────────────────────────
// 換算公式：F_pt = F_px * 595.28 / PRINT_WIDTH
// 在 820px 時：factor = 595.28 / 820 ≈ 0.726
//
// 關鍵字級上 A4 的對照（820px 基準）：
//   .num       14px → 10.2pt  ✓ 舒適可讀
//   .num-gold  15px → 10.9pt  ✓
//   thead th   12px →  8.7pt  ✓（臨界可讀）
//   thead small 10px →  7.3pt  ✗（太小；PRINT_CSS 升至 12px → 8.7pt）
//   .desc      10px →  7.3pt  ✗（太小；PRINT_CSS 升至 13px → 9.4pt）
//   .zh        16px → 11.6pt  ✓
//   .eng/.proc 12px →  8.7pt  ✓
//   .spec-name 13px →  9.4pt  ✓
//   .weight-tag 12px → 8.7pt  ✓
//   .saving-val 13px → 9.4pt  ✓
//
// 為何選 820 而非 800：
//   800 的 factor ≈ 0.744；820 的 factor ≈ 0.726。
//   差距僅 2.4%，但 820px 讓 V1 表格欄寬更接近螢幕比例（col-spec 30% = 246px），
//   數字欄（22% = 180px）在 html2canvas 3× scale 後仍有足夠像素密度，
//   且 colspan 最右欄（升金卡省/kg，26% = 213px）文案不會被迫換行。
//   關鍵字級如 .desc、thead small 透過 PRINT_CSS 補升，所有元素都落在 ≥8.5pt，
//   整體可讀性與版面還原度的平衡優於更窄的 800px。
//
export const PRINT_WIDTH = 820;

// ─── PRINT_CSS ───────────────────────────────────────────────────────────────
// 這份 CSS 透過 <style> 標籤注入到離屏 .pdf-print-root 容器中。
// 所有規則都以 .pdf-print-root 為 scope，不污染主應用。
//
// 處理重點：
// 1. Full-bleed：容器與內容 0 side-padding，無 max-width 限制
// 2. 可讀性補升：針對換算後 < 8.5pt 的選擇器，強制放大 font-size
// 3. 保留表格結構（不重排），只修尺寸與間距
// 4. 商品卡間距：margin-bottom 維持讓 A4 分頁後仍看得出卡的邊界
//
export const PRINT_CSS = `
  /* ── 根容器：全出血、白底 ──────────────────────────────── */
  .pdf-print-root {
    width: 100%;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    background: #ffffff;
  }

  /* App 層的 padding / max-width 不會進來（我們 clone 的是 product list，
     但保留這條作為防禦性重置，避免任何祖先元素帶入 padding） */
  .pdf-print-root *,
  .pdf-print-root *::before,
  .pdf-print-root *::after {
    box-sizing: border-box;
  }

  /* ── 商品卡外層容器（V1 的 V1Wrapper）：確保全寬、卡間留白 ─ */
  /* Emotion 產生的 class 是動態 hash，無法直接選；
     改從結構特徵選：pdf-print-root 的直接子 div（每支豆一張卡）。
     margin-bottom 沿用原始 10px，不加大（密度優先）。 */
  .pdf-print-root > div {
    width: 100%;
    box-sizing: border-box;
    /* box-shadow 在 html2canvas 渲染正常，保留作為卡片視覺分層 */
    margin-bottom: 10px;
  }

  /* ── 表格全寬保障 ────────────────────────────────────────── */
  .pdf-print-root table {
    width: 100%;
    table-layout: fixed;
  }

  /* ── 可讀性補升①：風味描述 .desc ──────────────────────────
     原始 10px → A4 上約 7.3pt（820px 基準），低於可讀閾值 8.5pt。
     補升至 13px → 9.4pt，與 .spec-name 同級，維持密度感但不失讀性。
     V1 .product-head .name-block .desc
     V2 .desc（價格表下方）
     V3 .left-col .name-block .desc
     三版都用 .desc class，一條規則通吃。 */
  .pdf-print-root .desc {
    font-size: 13px !important;
    line-height: 1.55;
  }

  /* ── 可讀性補升②：表頭 small（NT$/kg 單位文字）─────────────
     原始 10px → 7.3pt，borderline。補升至 12px → 8.7pt，
     與 thead th 的 font-size 等高，單位文字不再比欄頭文字還小。 */
  .pdf-print-root thead th small {
    font-size: 12px !important;
  }

  /* ── 可讀性補升③：重量標籤 .weight-tag ────────────────────
     原始 12px → 8.7pt，邊界值。印刷後膠囊字體偏小，
     補升至 13px → 9.4pt，與 .spec-name 視覺一致，量價階梯更易掃讀。 */
  .pdf-print-root .weight-tag {
    font-size: 13px !important;
  }

  /* ── 可讀性補升④：V3 tier-head-cell 欄頭（10px → 7.3pt）───
     V3 清單型欄頭字級 10px 太小；補升至 12px → 8.7pt。 */
  .pdf-print-root .tier-head-cell,
  .pdf-print-root .spec-head,
  .pdf-print-root .saving-head {
    font-size: 12px !important;
  }

  /* ── 可讀性補升⑤：V3 .spec-cell / .data-cell / .saving-cell
     原始 11–12px → 8.0–8.7pt，稍補至 12–13px 保持整齊。 */
  .pdf-print-root .spec-cell {
    font-size: 12px !important;
  }
  .pdf-print-root .data-cell {
    font-size: 13px !important;
  }
  .pdf-print-root .saving-cell {
    font-size: 12px !important;
  }

  /* ── 買鈕隱藏：PDF 上「下單」按鈕無意義，隱藏節省版面 ────── */
  .pdf-print-root .buy-btn,
  .pdf-print-root .buy-link,
  .pdf-print-root .buy-btn-inline {
    display: none !important;
  }

  /* ── product-head 右側按鈕消失後，name-block 要撐滿 ─────── */
  .pdf-print-root .product-head {
    gap: 10px;
  }
  .pdf-print-root .product-head .name-block {
    flex: 1;
  }

  /* ── 強制保留印刷色彩（金卡底色、膠囊綠底在黑白 PDF 要可見）
     html2canvas 本身不吃 @media print，這裡直接設定 print-color-adjust
     作為防禦性補強（savePDF.js 的 onclone 邏輯是主要保障）。 */
  .pdf-print-root .gold,
  .pdf-print-root .gold-head,
  .pdf-print-root .weight-tag,
  .pdf-print-root .lbl-tier.gold-head,
  .pdf-print-root .tier-head-cell.gold-head {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* ── 行 hover 狀態在 PDF 截圖時不應留下任何底色 ────────────
     （html2canvas 截圖時若游標停在某行，hover 色可能被捕捉進去） */
  .pdf-print-root tbody tr:hover,
  .pdf-print-root .spec-row:hover,
  .pdf-print-root .tier-data-row:hover {
    background: inherit !important;
  }
  .pdf-print-root .tier-data-row:hover .data-cell.gold {
    background: #E8F0EB !important;
  }

  /* ── 強制桌機 V1 表格版型（手機下載 PDF 防破版）────────────────────────────
     問題：手機裝置 viewport ≤768px，product.js 的 @media(max-width:768px) 行動版
       「卡片重排」規則會套到離屏列印容器 → PDF 變成手機版（圖下大片空白、價格用
       ::before 標籤堆疊、右側欄被裁切）。@media 看的是「裝置 viewport」而非容器寬度，
       即使容器固定 820px 仍會中行動版規則。
     解法：用 !important 把 .pdf-print-root 內 V1 表格還原成桌機表格版型；
       這同時修正「容器實際 layout（getSafeCutYs 量測）」與「html2canvas 截圖」，
       任何裝置下載結果都一致。 */
  .pdf-print-root .product-head { display: flex !important; }
  .pdf-print-root .product-head::after { content: none !important; }
  .pdf-print-root .product-head .thumb { float: none !important; margin: 0 !important; }
  .pdf-print-root .product-head .name-block { float: none !important; }

  .pdf-print-root .spec-table { display: table !important; table-layout: fixed !important; }
  .pdf-print-root .spec-table colgroup { display: table-column-group !important; }
  .pdf-print-root .spec-table thead { display: table-header-group !important; }
  .pdf-print-root .spec-table tbody { display: table-row-group !important; }
  .pdf-print-root .spec-table tr { display: table-row !important; }
  .pdf-print-root .spec-table th,
  .pdf-print-root .spec-table td {
    display: table-cell !important;
    border-top: none !important;
    border-bottom: 1px solid #DCDCDC !important;
    margin: 0 !important;
  }
  /* 行動版把欄位重排為 grid 區塊並用 ::before 帶出欄頭文字；桌機表格還原後要關掉 */
  .pdf-print-root .spec-table td.col-tier::before,
  .pdf-print-root .spec-table td.col-saving::before { content: none !important; }
  .pdf-print-root .spec-table td.col-spec { text-align: left !important; }
  .pdf-print-root .spec-table td.col-tier { text-align: center !important; flex-direction: row !important; }
  .pdf-print-root .spec-table td.col-saving { text-align: right !important; }
  /* 行動版把重量膠囊右邊距歸零（改用 flex gap）；桌機表格還原後補回 */
  .pdf-print-root .spec-table .weight-tag { margin-right: 8px !important; }
  /* 價格數字統一桌機字級（行動版放大為 15/16，PDF 用 14/15 維持密度一致） */
  .pdf-print-root .spec-table .num { font-size: 14px !important; }
  .pdf-print-root .spec-table .num-gold { font-size: 15px !important; }

  /* 注意：列印密度（每頁顯示幾支）改由 buildDensityCss() 依版本動態產生，
     疊在這份 PRINT_CSS 之後。見下方。 */
`;

// ─── 列印密度預設 ──────────────────────────────────────────────────────────────
// buildDensityCss(cfg) 產生「每頁約 N 支」的 V1 卡尺寸覆寫字串（疊在 PRINT_CSS 之後，
// 後出現→同特異度勝出）。主要槓桿：縮圖大小 + product-head / 表格 padding + 卡間距，
// 卡越小、padding 越緊 → 每張 V1 卡越矮 → A4 一頁容得下越多支。
// 5 支版另把風味描述夾行（descClamp）以壓低每張卡高度、讓每頁支數穩定。
//
// cfg: { thumb, headPadV, headPadH, cellPadV, cellPadH, cardGap, descClamp }
export function buildDensityCss(cfg) {
  const {
    thumb,
    headPadV = 8,
    headPadH = 10,
    cellPadV = 4,
    cellPadH = 10,
    cardGap = 8,
    descClamp = 0,
  } = cfg || {};

  const clampRule =
    descClamp > 0
      ? `.pdf-print-root .product-head .name-block .desc {
           display: -webkit-box;
           -webkit-box-orient: vertical;
           -webkit-line-clamp: ${descClamp};
           overflow: hidden;
         }`
      : '';

  return `
    .pdf-print-root .product-head {
      padding: ${headPadV}px ${headPadH}px;
      gap: 10px;
    }
    .pdf-print-root .product-head .thumb {
      flex: 0 0 ${thumb}px;
    }
    .pdf-print-root .product-head .thumb img {
      width: ${thumb}px;
      height: ${thumb}px;
    }
    .pdf-print-root .spec-table th,
    .pdf-print-root .spec-table td {
      padding: ${cellPadV}px ${cellPadH}px;
    }
    .pdf-print-root .js-product-list > div {
      margin-bottom: ${cardGap}px;
    }
    ${clampRule}
  `;
}
